export { NOTIFICATION_CATEGORY_SYSTEM } from "@repo/fhir-systems/code-systems";

import { NOTIFICATION_CATEGORY_SYSTEM } from "@repo/fhir-systems/code-systems";
import { NOTIFICATION_KEY_SYSTEM } from "@repo/fhir-systems/identifiers";
// @repo/notify — the inbox-item mint mapping: Report → Communication, wrapped
// in a transaction-bundle entry conditional on the notification key. Dedup is
// server-enforced via `ifNoneExist` — this package never searches before
// minting.

import type { BundleEntry, Communication } from "@medplum/fhirtypes";
import { notificationKey, parseNotificationKey } from "./notification-key";
import type { KindConfig } from "./registry";
import type { Report, ResourceRef } from "./types";

/** Unread inbox items carry this status; `markRead` moves them to `completed`. */
export const UNREAD_STATUS = "in-progress";

/** Search param for narrowing to a recipient's inbox. */
export const RECIPIENT_SEARCH_PARAM = "recipient";
/** Search param for narrowing to unread items. */
export const STATUS_SEARCH_PARAM = "status";

function refString(ref: ResourceRef): string {
  return `${ref.resourceType}/${ref.id}`;
}

/** Predicate mirroring the storage's unread status, for callers that filter in-memory. */
export function isUnread(communication: Communication): boolean {
  return communication.status === UNREAD_STATUS;
}

/** Scoping predicate for the inbox cutover: true for notification-category rows
 *  (the ones this package mints), false for anything else sharing the
 *  Communication resource type — e.g. OutboundMessage-category rows. Callers
 *  (the cutover deletion script) use this instead of duplicating the category
 *  system check. */
export function isNotificationCommunication(communication: Communication): boolean {
  return (
    communication.category?.some((cat) =>
      cat.coding?.some((coding) => coding.system === NOTIFICATION_CATEGORY_SYSTEM),
    ) ?? false
  );
}

/**
 * The kind a row was minted for, found by SYSTEM rather than by position:
 * Communication is co-tenanted (ADR-0001), so a row can carry categories this
 * package didn't write and `category[0]` is whichever was serialized first.
 *
 * Undefined for a row this package didn't mint.
 */
export function notificationKind(communication: Communication): string | undefined {
  for (const category of communication.category ?? []) {
    for (const coding of category.coding ?? []) {
      if (coding.system === NOTIFICATION_CATEGORY_SYSTEM) return coding.code;
    }
  }
  return undefined;
}

/** The source pointer, split once so no reader does it again. */
export function notificationSource(
  communication: Communication,
): { reference: string; resourceType: string; id: string } | undefined {
  const reference = communication.basedOn?.[0]?.reference;
  if (!reference) return undefined;
  const [resourceType, id] = reference.split("/");
  if (!resourceType || !id) return undefined;
  return { reference, resourceType, id };
}

/**
 * When the fact happened, for the kinds whose config declares an occurrence.
 *
 * Recovered from the notification key because that's where the mint puts it.
 * Readers use this to anchor a surface at the triggering moment (the note a
 * mention was about) rather than just opening the source.
 */
export function notificationOccurrence(communication: Communication): string | undefined {
  const key = communication.identifier?.find((id) => id.system === NOTIFICATION_KEY_SYSTEM)?.value;
  return key ? parseNotificationKey(key)?.occurrence : undefined;
}

export function mintInboxEntry(report: Report, kinds: Record<string, KindConfig>): BundleEntry {
  const key = notificationKey(report, kinds);

  const communication: Communication = {
    resourceType: "Communication",
    status: UNREAD_STATUS,
    category: [{ coding: [{ system: NOTIFICATION_CATEGORY_SYSTEM, code: report.kind }] }],
    basedOn: [{ reference: refString(report.source) }],
    recipient: [{ reference: refString(report.recipient) }],
    sent: new Date().toISOString(),
    identifier: [{ system: NOTIFICATION_KEY_SYSTEM, value: key }],
  };

  if (report.actor) {
    communication.sender = { reference: refString(report.actor) };
  }

  return {
    resource: communication,
    request: {
      method: "POST",
      url: "Communication",
      ifNoneExist: `identifier=${NOTIFICATION_KEY_SYSTEM}|${key}`,
    },
  };
}

export { NOTIFICATION_KEY_SYSTEM };
